// frontend/src/components/PlayerSearch.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { searchPlayers } from '../services/nbaApi';

const PlayerSearch = ({ onSelectPlayer, className = '' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  const doSearch = useCallback(async (term) => {
    if (term.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await searchPlayers(term);
      setResults(res.data || []);
      setIsOpen(true);
      setHighlightIdx(-1);
    } catch (err) {
      console.error('Player search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleSelect = (player) => {
    setQuery(`${player.first_name} ${player.last_name}`);
    setIsOpen(false);
    setResults([]);
    onSelectPlayer(player);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Enter') {
        doSearch(query);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(results[highlightIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Input */}
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-5 w-5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Search NBA player (e.g., LeBron James)"
          className="w-full pl-10 pr-10 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
        />
        {loading && (
          <Loader2 className="absolute right-10 h-5 w-5 text-orange-400 animate-spin" />
        )}
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && results.length > 0 && (
        <ul className="absolute z-50 mt-2 w-full bg-gray-800 border border-gray-600/50 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
          {results.map((player, idx) => (
            <li
              key={player.id}
              onClick={() => handleSelect(player)}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                idx === highlightIdx
                  ? 'bg-orange-500/20 text-white'
                  : 'text-gray-200 hover:bg-gray-700/50'
              } ${idx === 0 ? 'rounded-t-xl' : ''} ${
                idx === results.length - 1 ? 'rounded-b-xl' : ''
              }`}
            >
              <div>
                <span className="font-semibold">
                  {player.first_name} {player.last_name}
                </span>
                {player.position && (
                  <span className="ml-2 text-sm text-gray-400">{player.position}</span>
                )}
              </div>
              <div className="flex items-center gap-2 text-sm">
                {player.team && (
                  <span className="bg-gray-700 text-gray-300 px-2 py-0.5 rounded text-xs font-medium">
                    {player.team.abbreviation}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* No results message */}
      {isOpen && results.length === 0 && !loading && query.length >= 2 && (
        <div className="absolute z-50 mt-2 w-full bg-gray-800 border border-gray-600/50 rounded-xl shadow-2xl p-4 text-center text-gray-400">
          No players found for "{query}"
        </div>
      )}
    </div>
  );
};

export default PlayerSearch;
